'use client';
import React from 'react';
import { usePathname } from 'next/navigation';
import { runningClientSide } from '@benzinga/utils';
import { isObject } from '@benzinga/utils';
import { QueryParams } from '../entities';
import { getURLParams, formatURL } from '../utils/url';

type QueryUpdateType = 'replace' | 'push';

export const useQueryParams = (): [
  QueryParams,
  (keys: QueryParams, locationUpdateType?: QueryUpdateType, append?: boolean) => QueryParams,
] => {
  const pathname = usePathname();
  const url = runningClientSide() ? undefined : pathname?.toString();

  const [params, setParams] = React.useState<QueryParams>(getURLParams(url));

  const updateWindowLocation = (url: string | URL, type?: QueryUpdateType) => {
    const updateArguments = [{}, '', url] as const;

    if (type === 'replace') {
      window?.history?.replaceState(...updateArguments);
    } else {
      window?.history?.pushState(...updateArguments);
    }
  };

  const formatQueryParamObject = (data: Record<string, any>): QueryParams => {
    if (data) {
      for (const key in data) {
        if (Object.prototype.hasOwnProperty.call(data, key)) {
          const value = data[key];

          if ((isObject(value) && !Array.isArray(value)) || (Array.isArray(value) && !value.length)) {
            delete data[key];
          }
        }
      }
    }

    return data;
  };

  const updateQueryParams = (keys: QueryParams, locationUpdateType?: QueryUpdateType, append = true): QueryParams => {
    const urlQueryParams = getURLParams();

    const queryParams = formatQueryParamObject({
      ...(append ? urlQueryParams : {}),
      ...keys,
    });

    updateWindowLocation(formatURL(queryParams), locationUpdateType);

    setParams(queryParams);

    return queryParams;
  };

  return [params, updateQueryParams];
};
